import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PageLoader from "../components/PageLoader";
import EventLegend from "../components/EventLegend";

type EventItem = {
  id: string;
  title: string;
  type: string;
  location: string | null;
  date: string;
  time: string | null;
  endTime: string | null;
  description: string;
};

type EventParticipant = {
  name: string;
  notes: string;
};

type ParticipationEvent = {
  eventId: string;
  date: string;
  participants: EventParticipant[];
};

type ApplicationForm = {
  name: string;
  notes: string;
  sending: boolean;
  error: string;
  success: boolean;
};

const API_BASE =
  "https://sopronpogo-api.greenmeadow-9f8cab91.polandcentral.azurecontainerapps.io/api";

const EVENTS_URL = `${API_BASE}/events/get`;
const CALENDAR_FEED_URL = `${API_BASE}/events/calendar.ics`;
// Google's shortcut uses a subscription URI; keep HTTPS for the feed itself.
const WEB_CALENDAR_FEED_URL =
  CALENDAR_FEED_URL.replace(/^https?:\/\//i, "webcal://");
const GOOGLE_CALENDAR_URL =
  `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(WEB_CALENDAR_FEED_URL)}`;
const GOOGLE_CALENDAR_SETTINGS_URL =
  "https://calendar.google.com/calendar/u/0/r/settings/addbyurl";

const EVENTS_HEADING_STYLES = `
  .events-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  .events-heading > h1 {
    min-width: 0;
    margin: 0;
  }

  .events-google-calendar-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    box-sizing: border-box;
    border: 1px solid currentColor;
    border-radius: 12px;
    background: rgba(245, 207, 98, 0.08);
    color: #f5cf62;
    text-decoration: none;
  }

  .events-google-calendar-link:hover {
    background: rgba(245, 207, 98, 0.18);
    color: #ffe499;
  }

  .events-google-calendar-link:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 4px;
  }

  .events-calendar-help {
    margin: 0 0 1rem;
    font-size: 0.9rem;
  }

  .events-calendar-help > summary {
    width: fit-content;
    padding: 0.4rem 0;
    color: #f5cf62;
    cursor: pointer;
  }

  .events-calendar-help > p {
    max-width: 75ch;
    line-height: 1.5;
  }

  .events-calendar-help-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .events-calendar-help-controls > input {
    flex: 1 1 280px;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 0.7rem;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: inherit;
    font: inherit;
  }

  .events-calendar-help .events-calendar-help-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 44px;
    padding: 0.6rem 0.85rem;
    border: 1px solid currentColor;
    border-radius: 8px;
    background: rgba(245, 207, 98, 0.08);
    color: #f5cf62;
    font: inherit;
    text-decoration: none;
    cursor: pointer;
  }

  .events-calendar-help summary:focus-visible,
  .events-calendar-help input:focus-visible,
  .events-calendar-help-action:focus-visible {
    outline: 2px solid #f5cf62;
    outline-offset: 3px;
  }

  .events-calendar-help-status {
    min-height: 1.5em;
    margin: 0.5rem 0 0;
  }
`;

const TZ = "Europe/Budapest";
const HOUR = 60 * 60 * 1000;
const REGISTRATION_CLOSE_HOURS = 4;

const EMPTY_FORM: ApplicationForm = {
  name: "",
  notes: "",
  sending: false,
  error: "",
  success: false,
};

// Interpret event times in Budapest, regardless of the browser's time zone.
const wallFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function wallMilliseconds(instant: number): number {
  const parts = wallFormatter.formatToParts(new Date(instant));

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value);

  return Date.UTC(
    value("year"),
    value("month") - 1,
    value("day"),
    value("hour"),
    value("minute"),
    value("second")
  );
}

function eventDateTime(date: string, time: string | null): Date | null {
  if (!time || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

  const midnight = Date.parse(`${date}T00:00:00Z`);

  if (
    !Number.isFinite(midnight) ||
    new Date(midnight).toISOString().slice(0, 10) !== date
  ) {
    return null;
  }

  if (
    time !== "24:00" &&
    !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)
  ) {
    return null;
  }

  const [hours, minutes] = time.split(":").map(Number);
  const wall = midnight + hours * HOUR + minutes * 60_000;

  const offsets = new Set(
    [-24 * HOUR, 0, 24 * HOUR].map(
      delta => wallMilliseconds(wall + delta) - (wall + delta)
    )
  );

  const matches = Array.from(offsets)
    .map(offset => wall - offset)
    .filter(instant => wallMilliseconds(instant) === wall);

  // Reject nonexistent or ambiguous daylight-saving times.
  return matches.length === 1 ? new Date(matches[0]) : null;
}

function getEventCardStyle(event: EventItem): string {
  const type = (event.type ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  let style = "event-other";

  if (type === "movie night") {
    style = "movie-night";
  } else if (type === "munnamenza" || type === "munna menza") {
    style = "munnamenza";
  } else if (
    ["community event", "local community event", "sopron community event"].includes(type)
  ) {
    style = "community-event";
  } else if (/\b(?:max|dynamax|gigantamax)\b/.test(type)) {
    style = "event-max";
  } else if (/\braids?\b/.test(type)) {
    style = "event-raid";
  } else if (
    /\b(?:spotlight|community days?|research days?|incense days?|featured pokemon)\b/.test(type)
  ) {
    style = "event-featured";
  } else if (/\bfriendship\b/.test(type)) {
    style = "event-friendship";
  } else {
    style = "event-hatch";
  }

  return `event-themed ${style}`;
}

function eventElementId(event: EventItem): string {
  return `event-${encodeURIComponent(event.id)}-${event.date}`;
}

function CalendarSubscriptionHelp() {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [copyState, setCopyState] =
    useState<"idle" | "copied" | "failed">("idle");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(CALENDAR_FEED_URL);
      setCopyState("copied");
    } catch {
      input.current?.focus();
      input.current?.select();
      setCopyState("failed");
    }
  };

  return (
    <details className="events-calendar-help">
      <summary>
        {t("eventsPage.calendarHelp", "Calendar subscription help")}
      </summary>

      <p>
        {t(
          "eventsPage.calendarHelpInstructions",
          "If the calendar icon does not work, copy this link, open Google's From URL page, paste the link and choose Add calendar. Use a computer browser."
        )}
      </p>

      <div className="events-calendar-help-controls">
        <input
          ref={input}
          type="text"
          readOnly
          value={CALENDAR_FEED_URL}
          aria-label={t("eventsPage.calendarFeedUrl", "Calendar subscription URL")}
          onFocus={(event) => event.currentTarget.select()}
        />

        <button
          type="button"
          className="events-calendar-help-action"
          onClick={() => { void copyLink(); }}
        >
          {t("eventsPage.copyCalendarLink", "Copy calendar link")}
        </button>

        <a
          className="events-calendar-help-action"
          href={GOOGLE_CALENDAR_SETTINGS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("eventsPage.openCalendarFromUrl", "Google Calendar - From URL")}
        </a>
      </div>

      <p className="events-calendar-help-status" role="status">
        {copyState === "copied"
          ? t("eventsPage.calendarLinkCopied", "Calendar link copied.")
          : copyState === "failed"
            ? t(
                "eventsPage.calendarLinkCopyFailed",
                "Automatic copying is unavailable. Select and copy the link above."
              )
            : ""}
      </p>
    </details>
  );
}

function EventsCalendar({ events, now, locale, label }: {
  events: EventItem[];
  now: number;
  locale: string;
  label: string;
}) {
  // Reuse the page's Budapest clock, then do date-only arithmetic in UTC.
  const today = new Date(wallMilliseconds(now)).toISOString().slice(0, 10);
  const currentMonth = today.slice(0, 7);
  const showNextMonth = Number(today.slice(8, 10)) > 20;

  const { months, weekdays } = useMemo(() => {
    const [year, month] = currentMonth.split("-").map(Number);
    const monthFormat = new Intl.DateTimeFormat(locale, {
      year: "numeric", month: "long", timeZone: "UTC",
    });
    const dayFormat = new Intl.DateTimeFormat(locale, {
      year: "numeric", month: "long", day: "numeric",
      weekday: "long", timeZone: "UTC",
    });
    const shortWeekday = new Intl.DateTimeFormat(locale, {
      weekday: "short", timeZone: "UTC",
    });
    const fullWeekday = new Intl.DateTimeFormat(locale, {
      weekday: "long", timeZone: "UTC",
    });

    return {
      // 1 January 2024 was a Monday.
      weekdays: Array.from({ length: 7 }, (_, index) => {
        const date = new Date(Date.UTC(2024, 0, index + 1));
        return { short: shortWeekday.format(date), full: fullWeekday.format(date) };
      }),
      months: (showNextMonth ? [0, 1] : [0]).map(offset => {
        const first = new Date(Date.UTC(year, month - 1 + offset, 1));
        const key = first.toISOString().slice(0, 7);
        const leadingDays = (first.getUTCDay() + 6) % 7;
        const daysInMonth = new Date(
          Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)
        ).getUTCDate();

        return {
          key,
          title: monthFormat.format(first),
          days: Array.from({ length: 42 }, (_, index) => {
            const day = index - leadingDays + 1;
            if (day < 1 || day > daysInMonth) return null;
            const date = `${key}-${String(day).padStart(2, "0")}`;
            return {
              day,
              date,
              label: dayFormat.format(new Date(`${date}T00:00:00Z`)),
            };
          }),
        };
      }),
    };
  }, [currentMonth, locale, showNextMonth]);

  const eventsByDate = useMemo(() => {
    const grouped = new Map<string, { event: EventItem; style: string }[]>();
    events.forEach(event => {
      const dayEvents = grouped.get(event.date) ?? [];
      dayEvents.push({ event, style: getEventCardStyle(event) });
      grouped.set(event.date, dayEvents);
    });
    return grouped;
  }, [events]);

  const scrollToEvent = (event: EventItem) => {
    const target = document.getElementById(eventElementId(event));
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <nav className="events-calendar" data-months={months.length} aria-label={label}>
      {months.map(month => (
        <section
          key={month.key}
          className="events-calendar-month"
          aria-labelledby={`events-calendar-${month.key}`}
        >
          <h2 className="events-calendar-title" id={`events-calendar-${month.key}`}>
            {month.title}
          </h2>
          <div className="events-calendar-weekdays">
            {weekdays.map(day => (
              <abbr key={day.full} title={day.full}>{day.short}</abbr>
            ))}
          </div>
          <div className="events-calendar-grid">
            {month.days.map((day, index) => {
              if (!day) {
                return <span key={`blank-${index}`} className="events-calendar-blank" aria-hidden="true" />;
              }
              const dayEvents = eventsByDate.get(day.date) ?? [];
              const first = dayEvents[0];
              const titles = dayEvents.map(({ event }) =>
                `${event.time ? `${event.time} - ` : ""}${event.title}`
              );
              return (
                <button
                  key={day.date}
                  type="button"
                  className={`events-calendar-day${first ? ` has-events ${first.style}` : ""}`}
                  disabled={!first}
                  aria-current={day.date === today ? "date" : undefined}
                  aria-label={[day.label, ...titles].join("; ")}
                  title={[day.label, ...titles].join("\n")}
                  onClick={() => { if (first) scrollToEvent(first.event); }}
                >
                  <span className="events-calendar-number">{day.day}</span>
                  {dayEvents.length > 1 && (
                    <>
                      <span className="events-calendar-count" aria-hidden="true">
                        {dayEvents.length}
                      </span>
                      <span className="events-calendar-markers" aria-hidden="true">
                        {dayEvents.slice(0, 3).map(({ event, style }) => (
                          <span key={eventElementId(event)} className={`events-calendar-marker ${style}`} />
                        ))}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </nav>
  );
}

// Each API keeps its own forms, pending submissions, and stale-read protection.
function useEventParticipation(
  apiPath: "munnamenza" | "movienight",
  enabled: boolean
) {
  const { t } = useTranslation();

  const [participantEvents, setParticipantEvents] = useState<ParticipationEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);

  const [forms, setForms] = useState<Record<string, ApplicationForm>>({});

  const mounted = useRef(false);
  const readVersion = useRef(0);
  const pending = useRef(new Set<string>());
  const postControllers = useRef(new Set<AbortController>());

  const updateForm = (
    key: string,
    values: Partial<ApplicationForm>
  ) => {
    setForms(current => ({
      ...current,
      [key]: {
        ...EMPTY_FORM,
        ...current[key],
        ...values,
      },
    }));
  };

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
      postControllers.current.forEach(controller => controller.abort());
    };
  }, []);

  // Refresh participant lists every 30 seconds and when returning to the page.
  useEffect(() => {
    if (!enabled) return;

    const refresh = () => {
      if (pending.current.size === 0) {
        setReload(value => value + 1);
      }
    };

    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [enabled]);

  // Load the grouped participant lists from the simplified API.
  useEffect(() => {
    if (!enabled || pending.current.size > 0) return;

    const controller = new AbortController();
    const version = ++readVersion.current;
    let alive = true;

    (async () => {
      try {
        const response = await fetch(`${API_BASE}/${apiPath}/get`, {
          headers: { Accept: "application/json" },
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("Invalid participant list");
        }

        if (alive && version === readVersion.current) {
          setParticipantEvents(data);
          setLoaded(true);
          setLoadError(false);
        }
      } catch {
        if (alive && version === readVersion.current) {
          setLoadError(true);
        }
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [apiPath, enabled, reload]);

  const apply = async (ev: EventItem, key: string) => {
    if (pending.current.has(key)) return;

    const form = forms[key] ?? EMPTY_FORM;

    const startMs = eventDateTime(ev.date, ev.time)?.getTime();
    // Recheck the window at submission, not only when rendering.
    if (
      startMs === undefined ||
      Date.now() >= startMs - REGISTRATION_CLOSE_HOURS * HOUR
    ) {
      updateForm(key, {
        error: t(
          startMs === undefined
            ? "eventsPage.munnaMenza.timeUnavailable"
            : "eventsPage.munnaMenza.closed"
        ),
        success: false,
      });

      return;
    }

    if (!form.name.trim()) {
      updateForm(key, {
        error: t("eventsPage.munnaMenza.errors.invalid_name"),
        success: false,
      });

      return;
    }

    const request = {
      eventId: ev.id,
      date: ev.date,
      name: form.name.trim(),
      notes: form.notes.trim(),
    };

    pending.current.add(key);

    // Ignore GET responses that started before this submission.
    ++readVersion.current;

    updateForm(key, {
      sending: true,
      error: "",
      success: false,
    });

    const controller = new AbortController();
    postControllers.current.add(controller);

    try {
      const response = await fetch(`${API_BASE}/${apiPath}/add`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);

        throw new Error(
          response.status === 409
            ? t("eventsPage.munnaMenza.errors.duplicate")
            : body?.message ||
                t("eventsPage.munnaMenza.errors.submit_failed")
        );
      }

      if (mounted.current) {
        const participant = {
          name: request.name,
          notes: request.notes,
        };

        setParticipantEvents(current => {
          const existing = current.find(
            item => item.eventId === ev.id && item.date === ev.date
          );

          if (!existing) {
            return [
              ...current,
              {
                eventId: ev.id,
                date: ev.date,
                participants: [participant],
              },
            ];
          }

          return current.map(item =>
            item === existing
              ? {
                  ...item,
                  participants: [
                    ...(item.participants ?? []),
                    participant,
                  ],
                }
              : item
          );
        });

        updateForm(key, {
          name: "",
          notes: "",
          success: true,
        });
      }
    } catch (error) {
      if (mounted.current) {
        updateForm(key, {
          error:
            error instanceof Error && !(error instanceof TypeError)
              ? error.message
              : t("eventsPage.munnaMenza.errors.submit_failed"),
        });
      }
    } finally {
      pending.current.delete(key);
      postControllers.current.delete(controller);

      if (mounted.current) {
        updateForm(key, { sending: false });

        // Also refresh after a duplicate or an uncertain network result.
        if (pending.current.size === 0) {
          setReload(value => value + 1);
        }
      }
    }
  };

  return {
    events: participantEvents,
    loaded,
    loadError,
    forms,
    updateForm,
    apply,
    refresh: () => setReload(value => value + 1),
  };
}

export default function Events() {
  const { t, i18n } = useTranslation();

  const googleCalendarLabel = t(
    "eventsPage.addToGoogleCalendar",
    "Add to Google Calendar"
  );

  const locale = i18n.language.startsWith("de")
    ? "de-DE"
    : i18n.language.startsWith("hu")
      ? "hu-HU"
      : "en-US";

  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  const menza = useEventParticipation(
    "munnamenza",
    events?.some(event => event.type === "MunnaMenza") ?? false
  );

  const movieNight = useEventParticipation(
    "movienight",
    events?.some(event => event.type === "Movie Night") ?? false
  );

  // Update form visibility as registration closes.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Load the event calendar.
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const response = await fetch(EVENTS_URL, {
          headers: { Accept: "application/json" },
          cache: "no-cache",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (alive) {
          setEvents(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (alive) {
          setErr(error instanceof Error ? error.message : "fetch_failed");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  const list = useMemo(
    () =>
      (events ?? [])
        .map(ev => ({
          ev,
          start: eventDateTime(
            ev.date,
            ev.time ?? "00:00"
          )?.getTime(),
        }))
        .filter(item => item.start !== undefined && item.start >= now - HOUR)
        .sort((a, b) => (a.start ?? 0) - (b.start ?? 0))
        .map(item => item.ev),
    [events, now]
  );

  const fmtDate = (date: string) =>
    new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "short",
      timeZone: "UTC",
    }).format(new Date(`${date}T00:00:00Z`));

  const fmtTime = (time: string | null, date: string) => {
    const value = eventDateTime(date, time);

    return value
      ? new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: TZ,
        }).format(value)
      : null;
  };

  return (
    <div className="surface-body">
      <style>{EVENTS_HEADING_STYLES}</style>

      <div className="events-heading">
        <h1>{t("eventsPage.title")}</h1>

        <a
          className="events-google-calendar-link"
          href={GOOGLE_CALENDAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          title={googleCalendarLabel}
          aria-label={googleCalendarLabel}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M8 3v4M16 3v4M3 10h18M12 13v5M9.5 15.5h5" />
          </svg>
        </a>
      </div>

      <p className="events-intro">{t("eventsPage.intro")}</p>

      {!loading && !err && (
        <>
          <EventsCalendar
            events={list}
            now={now}
            locale={locale}
            label={t("eventsPage.title")}
          />
          <EventLegend />
        </>
      )}

      {loading && <PageLoader />}

      {err && (
        <p className="error" role="alert">
          {t("common.error")}: {err}
        </p>
      )}

      {!loading && !err && (
        list.length === 0 ? (
          <p style={{ opacity: 0.8 }}>{t("eventsPage.empty")}</p>
        ) : (
          <div className="events-list">
            {list.map(ev => {
              const start = fmtTime(ev.time, ev.date);
              const end = fmtTime(ev.endTime, ev.date);

              const timeLabel =
                !ev.time && !ev.endTime
                  ? t("eventsPage.labels.allday", "All day")
                  : start && end
                    ? `${start} - ${end}`
                    : start
                      ? start
                      : end
                        ? `${t("eventsPage.labels.until", "until")} ${end}`
                        : t("eventsPage.labels.tbd", "TBD");

              const participation =
                ev.type === "MunnaMenza" ? menza
                  : ev.type === "Movie Night" ? movieNight
                    : null;

              const cardStyle = getEventCardStyle(ev);

              const key = JSON.stringify([ev.type, ev.id, ev.date]);
              const fieldId = `participation-${encodeURIComponent(ev.type)}-${encodeURIComponent(ev.id)}-${ev.date}`;
              const form = participation?.forms[key] ?? EMPTY_FORM;

              const participants =
                participation?.events.find(
                  item => item.eventId === ev.id && item.date === ev.date
                )?.participants ?? [];

              const startMs = eventDateTime(ev.date, ev.time)?.getTime();

              const registrationOpen =
                startMs !== undefined &&
                now < startMs - REGISTRATION_CLOSE_HOURS * HOUR;

              return (
                <article
                  key={key}
                  id={eventElementId(ev)}
                  tabIndex={-1}
                  aria-labelledby={`${eventElementId(ev)}-title`}
                  className={`event-card ${cardStyle}`}
                >
                  <div className="event-head">
                    <h3 className="event-title" id={`${eventElementId(ev)}-title`}>
                      {ev.title}
                    </h3>

                    <span
                      className={`event-type-badge type-${(
                        ev.type || "Other"
                      ).toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {ev.type}
                    </span>
                  </div>

                  <div className="event-meta">
                    <span className="kv">
                      <span className="k">
                        {t("eventsPage.labels.location")}:
                      </span>
                      <span>
                        {ev.location ?? t("eventsPage.labels.tbd", "TBD")}
                      </span>
                    </span>

                    <span className="kv">
                      <span className="k">
                        {t("eventsPage.labels.date")}:
                      </span>
                      <span>{fmtDate(ev.date)}</span>
                    </span>

                    <span className="kv">
                      <span className="k">
                        {t("eventsPage.labels.time")}:
                      </span>
                      <span>{timeLabel}</span>
                    </span>
                  </div>

                  <p className="event-desc">{ev.description}</p>

                  {participation && (
                    <section className="munnamenza-panel">
                      {/* Application form comes first */}
                      {registrationOpen ? (
                        <form
                          className="munnamenza-form"
                          aria-busy={form.sending}
                          onSubmit={(e) => {
                            e.preventDefault();
                            void participation.apply(ev, key);
                          }}
                        >
                          <div className="munnamenza-field munna-name-field">
                            <label htmlFor={`${fieldId}-name`}>
                              {t("eventsPage.munnaMenza.name")}
                            </label>

                            <input
                              id={`${fieldId}-name`}
                              name="name"
                              type="text"
                              autoComplete="name"
                              required
                              value={form.name}
                              disabled={form.sending}
                              onChange={(e) =>
                                participation.updateForm(key, {
                                  name: e.target.value,
                                  error: "",
                                  success: false,
                                })
                              }
                            />
                          </div>

                          <div className="munnamenza-field munna-name-field">
                            <label htmlFor={`${fieldId}-notes`}>
                              {t("eventsPage.munnaMenza.notes")}
                            </label>

                            <input
                              id={`${fieldId}-notes`}
                              name="notes"
                              type="text"
                              value={form.notes}
                              disabled={form.sending}
                              onChange={(e) =>
                                participation.updateForm(key, {
                                  notes: e.target.value,
                                  error: "",
                                  success: false,
                                })
                              }
                            />
                          </div>

                          <button type="submit" disabled={form.sending}>
                            {t(
                              form.sending
                                ? "eventsPage.munnaMenza.sending"
                                : "eventsPage.munnaMenza.apply"
                            )}
                          </button>

                          <p className="munnamenza-form-note">
                            {t("eventsPage.munnaMenza.publicNote")}
                          </p>
                        </form>
                      ) : (
                        <p className="munnamenza-window-note">
                          {t(
                            startMs === undefined
                              ? "eventsPage.munnaMenza.timeUnavailable"
                              : "eventsPage.munnaMenza.closed"
                          )}
                        </p>
                      )}

                      {form.error && (
                        <p className="munnamenza-error" role="alert">
                          {form.error}
                        </p>
                      )}

                      {form.success && (
                        <p className="munnamenza-success" role="status">
                          {t("eventsPage.munnaMenza.success")}
                        </p>
                      )}

                      {/* Participants: collapsed by default, click to expand */}
                      <details className="munnamenza-participants">
                        <summary>
                          {t("eventsPage.munnaMenza.heading")}
                          {participation.loaded && ` (${participants.length})`}
                        </summary>

                        {!participation.loaded && !participation.loadError && (
                          <p role="status">
                            {t("eventsPage.munnaMenza.loading")}
                          </p>
                        )}

                        {participation.loadError && (
                          <p className="munnamenza-error" role="alert">
                            {t("eventsPage.munnaMenza.loadError")}{" "}
                            <button
                              type="button"
                              className="munnamenza-retry"
                              onClick={participation.refresh}
                            >
                              {t("eventsPage.munnaMenza.retry")}
                            </button>
                          </p>
                        )}

                        {participation.loaded && participants.length > 0 && (
                          <ul className="munnamenza-applicants">
                            {participants.map((participant, index) => (
                              <li key={`${participant.name}-${index}`}>
                                <span>{participant.name}</span>

                                {participant.notes && (
                                  <span>{participant.notes}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}

                        {participation.loaded && !participation.loadError && participants.length === 0 && (
                          <p>{t("eventsPage.munnaMenza.empty")}</p>
                        )}
                      </details>
                    </section>
                  )}
                </article>
              );
            })}
          </div>
        )
      )}
      <CalendarSubscriptionHelp />
    </div>
  );
}
